import React, { useState, useEffect } from 'react';
import { client } from '../client';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="text-center py-10 px-4 bg-red-100 border border-red-400 text-red-700 rounded">
    <p>Error loading contact information:</p>
    <p>{message}</p>
  </div>
);

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch contact page data from Sanity
  useEffect(() => {
    const query = `*[_type == "contactPage" && _id == "contactPageContent"][0]{
      title,
      contactInfoTitle,
      addressStreet,
      addressLocality,
      addressRegion,
      addressPostalCode,
      phone,
      fax,
      email,
      schoolHours,
      officeHours,
      formSectionTitle,
      formIntroText,
      mapSectionTitle,
      mapLocation,
      staffDirectorySectionTitle,
      staffDirectoryIntroText,
      staffSnippets
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        setPageData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching contact page data:', err);
        setError('Failed to fetch page content. Please try again later.');
        setLoading(false);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // console.log('Form submitted:', formData); // Keep existing submit logic
    alert('Thank you for your message. We will get back to you soon!');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  // Map configuration for Google Maps
  const mapContainerStyle = {
    height: "400px",
    width: "100%"
    // No z-index needed here initially, we can add if the problem persists
  };

  // Use lat/lng format for Google Maps center
  const center = pageData?.mapLocation ? {
    lat: pageData.mapLocation.lat,
    lng: pageData.mapLocation.lng
  } : { lat: 40.579, lng: -74.151 }; // Fallback center

  // Get Google Maps API Key from environment variable
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!pageData) { // Check only for pageData
    return <ErrorMessage message="No contact data found." />;
  }

  return (
    // Added padding top for fixed navbar
    <div className="container mx-auto px-4 py-8 pt-28 md:pt-32 lg:pt-36">
      <h1 className="text-3xl font-bold text-primary mb-4 text-center">{pageData.title || 'Contact Us'}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-1">
          <section className="bg-white rounded-lg shadow-md p-6 h-full">
            <h2 className="text-2xl font-semibold mb-4">{pageData.contactInfoTitle || 'School Contact Information'}</h2>

            <div className="mb-6">
              <h3 className="text-xl font-medium text-primary mb-2">Address</h3>
              <p className="flex items-start mb-1">
                <span className="text-xl mr-3">📍</span>
                <span>
                  P.S. 052 John Thompson School<br />
                  {pageData.addressStreet}<br />
                  {pageData.addressLocality}, {pageData.addressRegion} {pageData.addressPostalCode}
                </span>
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-medium text-primary mb-2">Phone & Fax</h3>
              {pageData.phone && (
                <p className="flex items-center mb-1">
                  <span className="text-xl mr-3">📞</span>
                  <span>{pageData.phone}</span>
                </p>
              )}
              {pageData.fax && (
                <p className="flex items-center mb-1">
                  <span className="text-xl mr-3">📠</span>
                  <span>{pageData.fax}</span>
                </p>
              )}
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-medium text-primary mb-2">Email</h3>
              {pageData.email && (
                <p className="flex items-center mb-1">
                  <span className="text-xl mr-3">✉️</span>
                  <a href={`mailto:${pageData.email}`} className="hover:underline">{pageData.email}</a>
                </p>
              )}
            </div>

            <div>
              <h3 className="text-xl font-medium text-primary mb-2">School Hours</h3>
              {pageData.schoolHours && (
                <p className="flex items-center mb-1">
                  <span className="text-xl mr-3">🕒</span>
                  <span>{pageData.schoolHours}</span>
                </p>
              )}
              {pageData.officeHours && (
                <p className="flex items-center mb-1">
                  <span className="text-xl mr-3">🏫</span>
                  <span>{pageData.officeHours}</span>
                </p>
              )}
            </div>
          </section>
        </div>

        <div className="lg:col-span-2">
          <section className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-4">{pageData.formSectionTitle || 'Send Us a Message'}</h2>
            <p className="mb-6 text-gray-600">
              {pageData.formIntroText || 'If you have any questions or concerns, please fill out the form below and we\'ll get back to you as soon as possible.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <label htmlFor="name" className="block text-gray-700 font-medium mb-1">Full Name*</label>
                   <input
                     type="text"
                     id="name"
                     name="name"
                     value={formData.name}
                     onChange={handleChange}
                     required
                     className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                   />
                 </div>
                 <div>
                   <label htmlFor="email" className="block text-gray-700 font-medium mb-1">Email Address*</label>
                   <input
                     type="email"
                     id="email"
                     name="email"
                     value={formData.email}
                     onChange={handleChange}
                     required
                     className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                   />
                 </div>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <label htmlFor="phone" className="block text-gray-700 font-medium mb-1">Phone Number</label>
                   <input
                     type="tel"
                     id="phone"
                     name="phone"
                     value={formData.phone}
                     onChange={handleChange}
                     className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                   />
                 </div>
                 <div>
                   <label htmlFor="subject" className="block text-gray-700 font-medium mb-1">Subject*</label>
                   <select
                     id="subject"
                     name="subject"
                     value={formData.subject}
                     onChange={handleChange}
                     required
                     className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                   >
                     <option value="">Select a subject</option>
                     <option value="General Inquiry">General Inquiry</option>
                     <option value="Enrollment">Enrollment</option>
                     <option value="Transportation">Transportation</option>
                     <option value="School Programs">School Programs</option>
                     <option value="Other">Other</option>
                   </select>
                 </div>
               </div>
               <div>
                 <label htmlFor="message" className="block text-gray-700 font-medium mb-1">Message*</label>
                 <textarea
                   id="message"
                   name="message"
                   value={formData.message}
                   onChange={handleChange}
                   required
                   rows="5"
                   className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                 ></textarea>
               </div>
               <div className="flex justify-end">
                 <button
                   type="submit"
                   className="px-6 py-3 bg-primary text-white font-medium rounded-md hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                 >
                   Send Message
                 </button>
               </div>
            </form>
          </section>
        </div>
      </div>

      {/* Map Section using Google Maps */}
      <section className="bg-white rounded-lg shadow-md p-6 mb-10">
        <h2 className="text-2xl font-semibold mb-6">{pageData.mapSectionTitle || 'Find Us'}</h2>
        <div className="w-full h-[400px] bg-gray-200 rounded-md overflow-hidden">
          {googleMapsApiKey ? (
            <LoadScript googleMapsApiKey={googleMapsApiKey}>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={15}
              >
                {/* Add Marker only if mapLocation exists */}
                {pageData.mapLocation && <Marker position={center} />}
              </GoogleMap>
            </LoadScript>
          ) : (
            <div className="text-center text-red-500 p-4 flex items-center justify-center h-full">
              Google Maps API Key is missing. Please add it to your .env file.
            </div>
          )}
        </div>
      </section>

      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold mb-4">{pageData.staffDirectorySectionTitle || 'Staff Directory'}</h2>
        <p className="mb-4">
          {/* Staff directory link can be made dynamic later if needed */}
          {pageData.staffDirectoryIntroText || 'To contact specific staff members, please visit our'} <a href="/staff" className="text-primary hover:underline font-medium">Staff Directory</a> page.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pageData.staffSnippets?.map((staff) => (
            <div key={staff._key} className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-primary mb-1">{staff.staffTitle}</h3>
              {staff.staffEmail && <p>Email: <a href={`mailto:${staff.staffEmail}`} className="hover:underline">{staff.staffEmail}</a></p>}
              {staff.staffPhone && <p>Phone: {staff.staffPhone}</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ContactPage; 